import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, DirectionsRenderer, useJsApiLoader } from "@react-google-maps/api";

const MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ["places"];

// Dark "dispatch" styled map
const MAP_STYLE_DARK = [
  { elementType: "geometry", stylers: [{ color: "#0B1B33" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0B1B33" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9CA3AF" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1E3A8A" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#0B1B33" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#60A5FA" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#07152B" }] },
];
const MAP_STYLE_LIGHT = [
  { elementType: "geometry", stylers: [{ color: "#F5F5F0" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "labels", stylers: [{ visibility: "simplified" }] },
];

function pinSvg(color) {
  return {
    path: "M12 0C5.383 0 0 5.383 0 12c0 8.5 12 24 12 24s12-15.5 12-24C24 5.383 18.617 0 12 0z",
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
    scale: 1.2,
    anchor: { x: 12, y: 36 },
  };
}

function formatMin(seconds) {
  if (!seconds || seconds < 0) return null;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return `${h}h ${rem}m`;
}

export default function TripMap({
  pickup,
  dropoff,
  driver,
  theme = "dark",
  height = 320,
  onEta,         // optional: (etaInfo) => void
}) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: MAP_KEY || "",
    libraries: LIBRARIES,
  });

  const validPickup = pickup && isFinite(pickup.lat) && isFinite(pickup.lng);
  const validDropoff = dropoff && isFinite(dropoff.lat) && isFinite(dropoff.lng);
  const validDriver = driver && isFinite(driver.lat) && isFinite(driver.lng);

  const [directions, setDirections] = useState(null);
  const [eta, setEta] = useState(null);
  const lastKeyRef = useRef("");

  // Request Directions whenever coords meaningfully change
  useEffect(() => {
    if (!isLoaded || !window.google?.maps) return;
    if (!validPickup || !validDropoff) { setDirections(null); setEta(null); return; }

    const key = [
      validDriver ? `${driver.lat.toFixed(4)},${driver.lng.toFixed(4)}` : "-",
      `${pickup.lat.toFixed(4)},${pickup.lng.toFixed(4)}`,
      `${dropoff.lat.toFixed(4)},${dropoff.lng.toFixed(4)}`,
    ].join("|");
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;

    const svc = new window.google.maps.DirectionsService();
    const origin = validDriver
      ? { lat: driver.lat, lng: driver.lng }
      : { lat: pickup.lat, lng: pickup.lng };
    const waypoints = validDriver
      ? [{ location: { lat: pickup.lat, lng: pickup.lng }, stopover: true }]
      : [];
    const destination = { lat: dropoff.lat, lng: dropoff.lng };

    svc.route(
      { origin, destination, waypoints, travelMode: window.google.maps.TravelMode.DRIVING },
      (result, status) => {
        if (status !== "OK" || !result) { setDirections(null); setEta(null); return; }
        setDirections(result);
        const legs = result.routes?.[0]?.legs || [];
        const secToPickup = validDriver ? legs[0]?.duration?.value : null;
        const secPickupToDropoff = validDriver ? legs[1]?.duration?.value : legs[0]?.duration?.value;
        const info = {
          secToPickup,
          secPickupToDropoff,
          totalSec: (secToPickup || 0) + (secPickupToDropoff || 0),
          pickupDistance: validDriver ? legs[0]?.distance?.text : null,
          tripDistance: validDriver ? legs[1]?.distance?.text : legs[0]?.distance?.text,
          mode: validDriver ? "live" : "estimate",
        };
        setEta(info);
        onEta && onEta(info);
      }
    );
  }, [isLoaded, validPickup, validDropoff, validDriver,
      pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, driver?.lat, driver?.lng, onEta]);

  const center = useMemo(() => {
    if (validDriver) return { lat: driver.lat, lng: driver.lng };
    if (validPickup) return { lat: pickup.lat, lng: pickup.lng };
    if (validDropoff) return { lat: dropoff.lat, lng: dropoff.lng };
    return { lat: 32.7767, lng: -96.7970 };
  }, [validDriver, validPickup, validDropoff, driver, pickup, dropoff]);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    clickableIcons: false,
    styles: theme === "dark" ? MAP_STYLE_DARK : MAP_STYLE_LIGHT,
    backgroundColor: theme === "dark" ? "#0B1B33" : "#F5F5F0",
  }), [theme]);

  const onLoad = (map) => {
    if (!window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    let added = 0;
    if (validPickup) { bounds.extend({ lat: pickup.lat, lng: pickup.lng }); added++; }
    if (validDropoff) { bounds.extend({ lat: dropoff.lat, lng: dropoff.lng }); added++; }
    if (validDriver) { bounds.extend({ lat: driver.lat, lng: driver.lng }); added++; }
    if (added >= 2) map.fitBounds(bounds, 60);
    else if (added === 1) map.setZoom(14);
    else map.setZoom(11);
  };

  const rendererOptions = useMemo(() => ({
    suppressMarkers: true,
    preserveViewport: true,
    polylineOptions: {
      strokeColor: validDriver ? "#3A7BD5" : "#7C3AED",
      strokeOpacity: 0.9,
      strokeWeight: 5,
    },
  }), [validDriver]);

  if (!MAP_KEY) {
    return (
      <div className="w-full rounded-lg border border-white/10 bg-white/[0.02] grid place-items-center text-white/50 text-sm"
           style={{ height }}>
        Map unavailable — missing REACT_APP_GOOGLE_MAPS_API_KEY
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div className="w-full rounded-lg border border-white/10 bg-white/[0.02] grid place-items-center text-white/50 text-sm animate-pulse"
           style={{ height }}>
        Loading map…
      </div>
    );
  }

  const pickupEta = formatMin(eta?.secToPickup);
  const tripEta = formatMin(eta?.secPickupToDropoff);

  return (
    <div className="relative w-full rounded-lg overflow-hidden border border-white/10" style={{ height }} data-testid="trip-map">
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={12}
        onLoad={onLoad}
        options={mapOptions}
      >
        {directions && <DirectionsRenderer directions={directions} options={rendererOptions} />}
        {validPickup && (
          <Marker position={{ lat: pickup.lat, lng: pickup.lng }} icon={pinSvg("#2B4C3B")} title={`Pickup: ${pickup.label || ""}`} />
        )}
        {validDropoff && (
          <Marker position={{ lat: dropoff.lat, lng: dropoff.lng }} icon={pinSvg("#C55A44")} title={`Drop-off: ${dropoff.label || ""}`} />
        )}
        {validDriver && (
          <Marker
            position={{ lat: driver.lat, lng: driver.lng }}
            title="Driver"
            icon={{
              path: 0,
              fillColor: "#3A7BD5",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 3,
              scale: 9,
            }}
            zIndex={999}
          />
        )}
      </GoogleMap>

      {/* Live ETA overlay */}
      {validDriver && pickupEta && (
        <div className="absolute top-3 right-3 bg-brandblue text-white rounded-md px-3 py-2 shadow-lg" data-testid="map-eta-chip">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-80">Driver ETA</div>
          <div className="text-base font-heading font-bold leading-none mt-0.5">{pickupEta}</div>
          {eta?.pickupDistance && <div className="text-[10px] opacity-70 mt-0.5">{eta.pickupDistance} away</div>}
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold">
        {validPickup && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur px-2.5 py-1 text-white">
            <span className="h-2 w-2 rounded-full bg-forest"></span> Pickup
          </span>
        )}
        {validDropoff && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur px-2.5 py-1 text-white">
            <span className="h-2 w-2 rounded-full bg-terracotta"></span> Drop-off
          </span>
        )}
        {validDriver && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brandblue px-2.5 py-1 text-white">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-white/80 live-dot"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white"></span>
            </span>
            Driver live
          </span>
        )}
        {!validDriver && tripEta && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur px-2.5 py-1 text-white">
            Est. drive · {tripEta}{eta?.tripDistance ? ` · ${eta.tripDistance}` : ""}
          </span>
        )}
      </div>
    </div>
  );
}
